import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, CheckCheck } from 'lucide-react';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    limit
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    markNotificationAsRead,
    markAllNotificationsAsRead,
    notificationTitleOf
} from '../services/NotificationSystem';
import { useNavigate } from 'react-router-dom';
import ToastNotification, { useToastNotification, vibrateNotification } from './ToastNotification';

function NotificationBell({ user, integrated = false }) {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // 토스트 알림 훅
    const { toasts, showToast, dismissToast } = useToastNotification();

    // 이전 알림 ID 추적 (새 알림 감지용)
    const prevNotificationIds = useRef(new Set());
    const isFirstLoad = useRef(true);

    // 실시간 알림 구독
    useEffect(() => {
        if (!user?.uid) return;

        // ✅ 직원이면 companyId로, 아니면 uid로 조회
        const targetUserId = user.companyId || user.uid;

        const notificationsRef = collection(db, 'notifications');
        const q = query(
            notificationsRef,
            where('recipientId', '==', targetUserId),
            orderBy('createdAt', 'desc'),
            limit(10)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {

            const notificationsList = [];
            let unreadCounter = 0;
            const currentIds = new Set();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const notification = {
                    id: doc.id,
                    ...data
                };
                notificationsList.push(notification);
                currentIds.add(doc.id);

                if (!data.read) {
                    unreadCounter++;
                }

                // 새 알림 감지 및 토스트 표시
                if (!isFirstLoad.current && !prevNotificationIds.current.has(doc.id) && !data.read) {
                    // 새 알림이 도착함 - 토스트 표시
                    showToast({
                        id: doc.id,
                        type: data.type,
                        title: notificationTitleOf(data) || '새 알림',   // 공정 3단계 이름(구 '의뢰서 접수' → '의뢰서 확인', 2026-10-12)
                        message: data.message || '',
                        link: data.link,
                        duration: 6000,
                    });

                    // 모바일 진동
                    vibrateNotification();
                }
            });

            // 현재 알림 ID 저장
            prevNotificationIds.current = currentIds;
            isFirstLoad.current = false;

            setNotifications(notificationsList);
            setUnreadCount(unreadCounter);
            setLoading(false);
        }, (error) => {
            console.error('❌ onSnapshot 에러:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid, user?.companyId]);

    // 토스트 알림 클릭 처리
    const handleToastClick = useCallback(async (notification) => {
        try {
            if (!notification.read) {
                await markNotificationAsRead(notification.id);
            }
            if (notification.link) {
                navigate(notification.link);
            }
        } catch (error) {
            console.error('토스트 알림 처리 실패:', error);
        }
    }, [navigate]);

    // 알림 클릭 처리
    const handleNotificationClick = async (notification) => {
        try {
            if (!notification.read) {
                await markNotificationAsRead(notification.id);
            }

            if (notification.link) {
                navigate(notification.link);
                setShowDropdown(false);
            }
        } catch (error) {
            console.error('알림 처리 실패:', error);
        }
    };

    // 모두 읽음 처리
    const handleMarkAllAsRead = async () => {
        try {
            const targetUserId = user.companyId || user.uid;
            await markAllNotificationsAsRead(targetUserId);
        } catch (error) {
            console.error('전체 읽음 처리 실패:', error);
        }
    };

    // 시간 포맷팅
    const formatTime = (timestamp) => {
        if (!timestamp) return '';

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);

        if (diff < 60) return '방금 전';
        if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;

        return date.toLocaleDateString('ko-KR');
    };

    // 알림 타입별 아이콘 색상
    const getNotificationColor = (type) => {
        switch (type) {
            case 'payment_approved':
            case 'staff_approved':
                return '#10b981';
            case 'payment_rejected':
                return '#ef4444';
            case 'payment_approval_request':
                return '#f59e0b';
            case 'order_created':
            case 'order_status_changed':
                return '#3b82f6';
            // 치과 공정 3단계 알림 (2026-10-12) — 색은 shared STAGE3_COLOR 와 같게
            case 'order_received':      // 의뢰서 확인
                return '#4f46e5';
            case 'order_design_done':   // 디자인 완료 (신규 type)
                return '#0b6e5d';
            case 'case_packed':         // 포장 완료
                return '#64748b';
            case 'payment_completed':
                return '#8b5cf6';
            case 'staff_join_request':
                return '#f59e0b';
            case 'vendor_intake_hold':   // S5(10-27) 가입 승인 시 외주 수주 보류 안내(서버 발송, link /settings/outsource-vendor)
                return '#f59e0b';
            case 'promo_signup':
                return '#ec4899';
            case 'test':
                return '#10b981';
            default:
                return '#6b7280';
        }
    };

    return (
        <>
            {/* 토스트 알림 */}
            <ToastNotification
                notifications={toasts}
                onDismiss={dismissToast}
                onNotificationClick={handleToastClick}
            />

            <div style={styles.container}>
                <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    style={{
                        ...styles.bellButton,
                        ...(integrated ? styles.bellButtonIntegrated : {})
                    }}
                >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                        <span style={styles.badge}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>

            {showDropdown && (
                <>
                    <div 
                        style={styles.overlay}
                        onClick={() => setShowDropdown(false)}
                    />

                    <div style={styles.dropdown} className="notification-dropdown">
                        <div style={styles.header}>
                            <div style={styles.headerTitle}>
                                <Bell size={18} />
                                <span>알림</span>
                            </div>
                            <div style={styles.headerActions}>
                                {unreadCount > 0 && (
                                    <button
                                        onClick={handleMarkAllAsRead}
                                        style={styles.markAllButton}
                                        title="모두 읽음"
                                    >
                                        <CheckCheck size={16} />
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowDropdown(false)}
                                    style={styles.closeButton}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div style={styles.notificationList}>
                            {loading ? (
                                <div style={styles.emptyState}>
                                    <div style={styles.spinner}></div>
                                    <p>로딩 중...</p>
                                </div>
                            ) : notifications.length === 0 ? (
                                <div style={styles.emptyState}>
                                    <Bell size={32} style={{ opacity: 0.3 }} />
                                    <p>알림이 없습니다</p>
                                </div>
                            ) : (
                                notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        style={{
                                            ...styles.notificationItem,
                                            ...(notification.read ? {} : styles.notificationItemUnread)
                                        }}
                                    >
                                        <div
                                            style={{
                                                ...styles.statusDot,
                                                backgroundColor: getNotificationColor(notification.type)
                                            }}
                                        />

                                        <div style={styles.notificationContent}>
                                            <div style={styles.notificationTitle}>
                                                {notificationTitleOf(notification)}
                                            </div>
                                            <div style={styles.notificationMessage}>
                                                {notification.message}
                                            </div>
                                            <div style={styles.notificationTime}>
                                                {formatTime(notification.createdAt)}
                                            </div>
                                        </div>

                                        {notification.read && (
                                            <Check size={14} style={styles.readIcon} />
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
            </div>
        </>
    );
}

const styles = {
    container: {
        position: 'relative',
    },
    bellButton: {
        position: 'relative',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        backgroundColor: '#f3f4f6',
        color: '#374151',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
    },
    bellButtonIntegrated: {
        backgroundColor: 'transparent',
        width: '36px',
        height: '36px',
    },
    badge: {
        position: 'absolute',
        top: '2px',
        right: '2px',
        backgroundColor: '#ef4444',
        color: 'white',
        fontSize: '10px',
        fontWeight: '600',
        padding: '2px 5px',
        borderRadius: '10px',
        minWidth: '18px',
        textAlign: 'center',
    },
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 998,
    },
    dropdown: {
        position: 'fixed',
        top: '60px',
        right: '16px',
        width: '360px',
        maxWidth: 'calc(100vw - 32px)',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        zIndex: 999,
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 80px)',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
    },
    headerTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
    },
    headerActions: {
        display: 'flex',
        gap: '8px',
    },
    markAllButton: {
        padding: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#6b7280',
        cursor: 'pointer',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.2s',
    },
    closeButton: {
        padding: '4px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#6b7280',
        cursor: 'pointer',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
    },
    notificationList: {
        maxHeight: '400px',
        overflowY: 'auto',
    },
    notificationItem: {
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        cursor: 'pointer',
        borderBottom: '1px solid #f3f4f6',
        transition: 'background-color 0.2s',
        position: 'relative',
    },
    notificationItemUnread: {
        backgroundColor: '#eff6ff',
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        marginTop: '6px',
        flexShrink: 0,
    },
    notificationContent: {
        flex: 1,
        minWidth: 0,
    },
    notificationTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '4px',
        wordBreak: 'break-word',
        lineHeight: '1.4',
    },
    notificationMessage: {
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '4px',
        whiteSpace: 'normal',
        lineHeight: '1.5',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
    },
    notificationTime: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    readIcon: {
        color: '#10b981',
        flexShrink: 0,
        marginTop: '6px',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        color: '#9ca3af',
        fontSize: '14px',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid #e5e7eb',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '12px',
    },
};

// CSS 스타일 추가 (중복 방지)
if (typeof window !== 'undefined' && !document.getElementById('notification-bell-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'notification-bell-styles';
    styleSheet.textContent = `
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
            .notification-dropdown {
                position: fixed !important;
                top: 60px !important;
                left: 8px !important;
                right: 8px !important;
                width: auto !important;
                max-width: none !important;
            }
        }
    `;
    document.head.appendChild(styleSheet);
}

export default NotificationBell;